/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { EntityStore } from 'src/core/types/EntityStore';
import { CustomTextTestSetDetailsTableRow } from 'src/customText/appShell/modelDetailsPages/tabs/CustomTextTestSetDetailsTab';
import {
    CustomTextVerificationDocumentStore,
    ExtractionLabelPredictionConflict
} from 'src/customText/core/stores/CustomTextVerificationDocumentStore';
import { AnnotationData } from 'src/packages/react-text-annotator/lib/esm';

export type RESULT_TYPE = 'FALSE_POSITIVE' | 'FALSE_NEGATIVE' | 'TRUE_POSITIVE' | 'TRUE_NEGATIVE' | 'CONFLICT' | 'NO_CONFLICT';

const calculateResultType = (entityStores: EntityStore[], entry: ExtractionLabelPredictionConflict): RESULT_TYPE => {
    const labelText = entityStores.find(entityStore => entityStore.id === entry.label?.entityId)?.name;
    const predictionText = entityStores.find(entityStore => entityStore.id === entry.prediction?.entityId)?.name;

    if (labelText) {
        if (labelText === predictionText) {
            return 'TRUE_POSITIVE';
        }

        return 'FALSE_NEGATIVE';
    }

    if (predictionText) {
        return 'FALSE_POSITIVE';
    }

    return 'TRUE_NEGATIVE';
};

const getResultTypeMessage = (
    result: RESULT_TYPE,
    truePositiveMessage: string,
    falsePositiveMessage: string,
    falseNegativeMessage: string
) => {
    switch (result) {
        case 'TRUE_POSITIVE':
            return truePositiveMessage;
        case 'FALSE_POSITIVE':
            return falsePositiveMessage;
        default:
            return falseNegativeMessage;
    }
};

export const getExtractionTableItems = (
    notLabeledMessage: string,
    notPredictedMessage: string,
    truePositiveMessage: string,
    falsePositiveMessage: string,
    falseNegativeMessage: string,
    entityStores: EntityStore[],
    documentStores: CustomTextVerificationDocumentStore[],
    showIncorrectPredictions: boolean
): CustomTextTestSetDetailsTableRow[] => {
    const items: CustomTextTestSetDetailsTableRow[] = [];

    documentStores.forEach(documentStore => {
        const displayedLabelPredictionList = showIncorrectPredictions
            ? documentStore.extractionConflicts
            : documentStore.labelPredictionMap;

        displayedLabelPredictionList?.forEach((entry: ExtractionLabelPredictionConflict) => {
            const text = documentStore.isInitialized
                ? documentStore.text.slice(
                      entry.label?.startCharIndex ?? entry.prediction?.startCharIndex,
                      entry.label?.endCharIndex ?? entry.prediction?.endCharIndex
                  )
                : '';

            const labelText: string = entityStores.find(entityStore => entityStore.id === entry.label?.entityId)?.name ?? notLabeledMessage;
            const predictionText: string =
                entityStores.find(entityStore => entityStore.id === entry.prediction?.entityId)?.name ?? notPredictedMessage;

            const resultType = calculateResultType(entityStores, entry);

            items.push({
                text,
                labelText,
                predictionText,
                label: entry.label,
                name: documentStore.name,
                prediction: entry.prediction,
                rowKey: `${documentStore.name}-${items.length}`,
                resultType: getResultTypeMessage(resultType, truePositiveMessage, falsePositiveMessage, falseNegativeMessage),
                isResultTruePositive: resultType === 'TRUE_POSITIVE'
            });
        });
    });

    return items;
};

export const getClassificationTableItems = (
    notLabeledMessage: string,
    notPredictedMessage: string,
    conflictMessage: string,
    noConflictMessage: string,
    documentStores: CustomTextVerificationDocumentStore[],
    showIncorrectPredictions: boolean
): CustomTextTestSetDetailsTableRow[] => {
    const items: CustomTextTestSetDetailsTableRow[] = [];

    documentStores.forEach(documentStore => {
        const labelText: string = (documentStore.classificationLabelPredictionObj?.label as string)?.trim().length
            ? (documentStore.classificationLabelPredictionObj?.label as string)
            : notLabeledMessage;
        const predictionText: string = (documentStore.classificationLabelPredictionObj?.prediction as string)?.trim().length
            ? (documentStore.classificationLabelPredictionObj?.prediction as string)
            : notPredictedMessage;

        const resultType: RESULT_TYPE = documentStore.classificationLabelPredictionObj?.isConflict ? 'CONFLICT' : 'NO_CONFLICT';

        if (!showIncorrectPredictions || resultType === 'CONFLICT') {
            items.push({
                text: null,
                labelText,
                predictionText,
                name: documentStore.name,
                rowKey: `${documentStore.name}-${items.length}`,
                isResultTruePositive: resultType === 'NO_CONFLICT',
                label: documentStore.classificationLabelPredictionObj?.label,
                prediction: documentStore.classificationLabelPredictionObj?.prediction,
                resultType: resultType === 'CONFLICT' ? conflictMessage : noConflictMessage
            });
        }
    });

    return items;
};

export const getPartOfDocument = (text: string, annotationData: AnnotationData): { text: string; annotation: AnnotationData } => {
    if (!text || !annotationData) {
        return {
            text,
            annotation: annotationData
        };
    }

    let annotationEndLineIndex = -1;
    let annotationStartLineIndex = -1;
    const lines = text.split('\n');
    const lineLengthSums: number[] = [];
    lines.map((line, index) => lineLengthSums.push(line.length + 1 + (index === 0 ? 0 : lineLengthSums[index - 1])));
    lineLengthSums.map((lineLength, index) => {
        if (annotationStartLineIndex === -1 && annotationData.startToken <= lineLength) {
            annotationStartLineIndex = index;
        }

        if (annotationEndLineIndex === -1 && annotationData.endToken <= lineLength) {
            annotationEndLineIndex = index;
        }
    });

    const newTextStartLine = Math.max(0, annotationStartLineIndex - 3);
    const newTextEndLine = Math.min(annotationEndLineIndex + 3, lines.length - 1);
    const annotationShift = newTextStartLine === 0 ? 0 : lineLengthSums[newTextStartLine - 1];

    return {
        text: text.substring(annotationShift, lineLengthSums[newTextEndLine]),
        annotation: {
            ...annotationData,
            endToken: annotationData.endToken - annotationShift,
            startToken: annotationData.startToken - annotationShift
        }
    };
};
